import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    } catch (error) {
        return dateString;
    }
};

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const toInputDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
        return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const calculateNoticePeriodDays = (resignationDate, lastWorkingDate) => {
    if (!resignationDate || !lastWorkingDate) return '';

    const start = new Date(resignationDate);
    const end = new Date(lastWorkingDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
        return '';
    }

    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return '';
    return `${diffDays} days`;
};

const STATUS_OPTIONS = ['Pending', 'Hold', 'Reject', 'Approved', 'Completed'];

const normalizeStatus = (value) => {
    if (!value) return 'Pending';
    if (value === 'Rejected') return 'Reject';
    return STATUS_OPTIONS.includes(value) ? value : 'Pending';
};

const getStatusClass = (status) => {
    if (!status || status === '-') return 'pending';
    const normalized = String(status).toLowerCase().trim();
    if (normalized.includes('reject')) return 'reject';
    if (normalized.includes('hold')) return 'hold';
    if (normalized.includes('approv')) return 'approved';
    if (normalized.includes('completed') || normalized.includes('complete')) return 'completed';
    if (normalized.includes('pending')) return 'pending';
    return normalized.replace(/\s+/g, '-');
};

const ApplicationStatusView = () => {
    const { id: employeeMongoId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const resignationFromState = location.state?.resignation;

    const [resignation, setResignation] = useState(resignationFromState || null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [lastWorkingDate, setLastWorkingDate] = useState('');
    const [noticePeriod, setNoticePeriod] = useState('');
    const [status, setStatus] = useState('Pending');
    const [comment, setComment] = useState('');

    useEffect(() => {
        if (!employeeMongoId) {
            toast.error('Employee identifier is missing.');
            navigate('/application-status');
            return;
        }
        fetchResignation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeMongoId]);

    const isApprovedStatus = status === 'Approved';

    useEffect(() => {
        if (!isApprovedStatus) {
            setNoticePeriod('');
            return;
        }
        if (resignation?.resignationDate && lastWorkingDate) {
            setNoticePeriod(
                calculateNoticePeriodDays(resignation.resignationDate, lastWorkingDate)
            );
        } else {
            setNoticePeriod('');
        }
    }, [lastWorkingDate, resignation?.resignationDate, isApprovedStatus]);

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setStatus(newStatus);
        if (newStatus !== 'Approved') {
            setLastWorkingDate('');
            setNoticePeriod('');
        } else if (resignation) {
            setLastWorkingDate(toInputDate(resignation.lastWorkingDate));
            setNoticePeriod(resignation.noticePeriod || '');
        }
    };

    const fetchResignation = async () => {
        const userId = localStorage.getItem('id');

        if (!userId) {
            toast.error('User ID not found. Please login again.');
            navigate('/application-status');
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'get-resignations',
                id: userId,
                employeeMongoId,
                page: 1,
                limit: 25
            };

            const response = await axios.post(API_URL, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            const result = response.data;

            if (result?.success) {
                const list = result.data || [];
                const match =
                    (resignationFromState?.id && list.find((r) => r.id === resignationFromState.id)) ||
                    list[0];

                if (match) {
                    const normalizedStatus = normalizeStatus(match.status);
                    setResignation(match);
                    setStatus(normalizedStatus);
                    if (normalizedStatus === 'Approved') {
                        setLastWorkingDate(toInputDate(match.lastWorkingDate));
                        setNoticePeriod(match.noticePeriod || '');
                    } else {
                        setLastWorkingDate('');
                        setNoticePeriod('');
                    }
                    const latestComment = Array.isArray(match.comments) && match.comments.length > 0
                        ? match.comments[match.comments.length - 1].comment
                        : '';
                    setComment(match.comment || latestComment || '');
                } else {
                    toast.error('Resignation application not found');
                    navigate('/application-status');
                }
            } else {
                toast.error(result?.message || 'Failed to fetch resignation details');
            }
        } catch (error) {
            console.error('Error fetching resignation:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleLastWorkingDateChange = (e) => {
        setLastWorkingDate(e.target.value);
    };

    const handleSave = async () => {
        if (!resignation?.id) {
            toast.error('Resignation record not found.');
            return;
        }

        let computedNotice = '';
        if (isApprovedStatus) {
            if (!lastWorkingDate) {
                toast.error('Please select last working date');
                return;
            }
            computedNotice = calculateNoticePeriodDays(resignation.resignationDate, lastWorkingDate);
            if (!computedNotice) {
                toast.error('Last working date must be on or after resignation date');
                return;
            }
        }

        const updatedBy = localStorage.getItem('id');
        if (!updatedBy) {
            toast.error('User ID not found. Please login again.');
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                action: 'update-resignation',
                resignationId: resignation.id,
                id: updatedBy,
                status,
                lastWorkingDate: isApprovedStatus ? formatDateToDDMMYYYY(lastWorkingDate) : '',
                noticePeriod: isApprovedStatus ? computedNotice : '',
                comment: comment.trim()
            };

            const response = await axios.post(API_URL, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            const result = response.data;

            if (result?.success) {
                toast.success(result?.message || 'Resignation updated successfully');
                navigate('/application-status');
            } else {
                toast.error(result?.message || 'Failed to update resignation');
            }
        } catch (error) {
            console.error('Error updating resignation:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const minLastWorkingDate = resignation?.resignationDate
        ? toInputDate(resignation.resignationDate)
        : undefined;

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                View Resignation Application
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Review resignation details and set last working date to calculate notice period.
                            </p>
                        </div>
                        <div className="col-md-4 employee-view-header-actions">
                            <button
                                type="button"
                                className="employee-back-btn"
                                onClick={() => navigate('/application-status')}
                            >
                                Back
                            </button>
                        </div>
                    </div>

                    <div className="card employee-content-card">
                        <div className="card-body">
                            {loading ? (
                                <Loading size={120} />
                            ) : resignation ? (
                                <>
                                    <div className="mb-4">
                                        <div className="resignation-view-status-row">
                                            <h4 className="section-title">Application Status</h4>
                                            <span className={`employee-status employee-status-${getStatusClass(status)}`}>
                                                {status || 'Pending'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="section-title">Employee Information</h4>
                                        <p className="section-subtitle">Details submitted with the resignation request.</p>
                                        <div className="employee-detail-grid two-column">
                                            <div className="employee-detail-item">
                                                <span className="employee-detail-label">Employee Name</span>
                                                <span className="employee-detail-value">{resignation.employeeName || '-'}</span>
                                            </div>
                                            <div className="employee-detail-item">
                                                <span className="employee-detail-label">Employee ID</span>
                                                <span className="employee-detail-value">{resignation.employeeId || '-'}</span>
                                            </div>
                                            <div className="employee-detail-item">
                                                <span className="employee-detail-label">Department</span>
                                                <span className="employee-detail-value">{resignation.department || '-'}</span>
                                            </div>
                                            <div className="employee-detail-item">
                                                <span className="employee-detail-label">Personal Email</span>
                                                <span className="employee-detail-value">{resignation.personalEmail || '-'}</span>
                                            </div>
                                            <div className="employee-detail-item">
                                                <span className="employee-detail-label">Personal Number</span>
                                                <span className="employee-detail-value">{resignation.personalNumber || '-'}</span>
                                            </div>
                                            <div className="employee-detail-item">
                                                <span className="employee-detail-label">Resignation Date</span>
                                                <span className="employee-detail-value">{formatDate(resignation.resignationDate)}</span>
                                            </div>
                                            <div className="employee-detail-item" style={{ gridColumn: '1 / -1' }}>
                                                <span className="employee-detail-label">Reason</span>
                                                <span className="employee-detail-value">{resignation.reason || '-'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-row-3">

                                            <div className="form-group">
                                                <label htmlFor="resignationStatus" className="form-label">
                                                    Status<span className="required-star">*</span>
                                                </label>
                                                <select
                                                    id="resignationStatus"
                                                    className="form-control"
                                                    value={status}
                                                    onChange={handleStatusChange}
                                                >
                                                    {STATUS_OPTIONS.map((option) => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="lastWorkingDate" className="form-label">
                                                    Last Working Date
                                                    {isApprovedStatus && <span className="required-star">*</span>}
                                                </label>
                                                <input
                                                    type="date"
                                                    id="lastWorkingDate"
                                                    className="form-control"
                                                    value={lastWorkingDate}
                                                    onChange={handleLastWorkingDateChange}
                                                    min={minLastWorkingDate}
                                                    disabled={!isApprovedStatus}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Notice Period</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={isApprovedStatus ? (noticePeriod || '-') : '-'}
                                                    readOnly
                                                    disabled={!isApprovedStatus}
                                                    placeholder="Select last working date"
                                                />
                                                {isApprovedStatus && lastWorkingDate && noticePeriod && (
                                                    <small className="text-muted" style={{ marginTop: '6px', display: 'block' }}>
                                                        Calculated from resignation date to last working date
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-group">
                                            <label htmlFor="resignationComment" className="form-label">Comment</label>
                                            <textarea
                                                id="resignationComment"
                                                className="form-control"
                                                rows={4}
                                                value={comment}
                                                onChange={(e) => setComment(e.target.value)}
                                                placeholder="e.g. Approved after discussion with HR"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-actions">
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => navigate('/application-status')}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-primary"
                                            onClick={handleSave}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
                .resignation-view-status-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    flex-wrap: wrap;
                }
                .employee-status-pending {
                    background-color: #fef3c7;
                    color: #92400e;
                }
                .employee-status-hold {
                    background-color: #e0e7ff;
                    color: #3730a3;
                }
                .employee-status-approved {
                    background-color: #d1fae5;
                    color: #065f46;
                }
                .employee-status-reject {
                    background-color: #fee2e2;
                    color: #991b1b;
                }
                .employee-status-completed {
                    background-color: #dbeafe;
                    color: #1e40af;
                }
                .comment-history-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .comment-history-item {
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 10px 12px;
                    background: #fff;
                }
                .comment-history-meta {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    font-size: 12px;
                    color: #6b7280;
                    margin-bottom: 6px;
                }
                .comment-history-text {
                    font-size: 14px;
                    color: #111827;
                    white-space: pre-wrap;
                    word-break: break-word;
                }
            `}</style>
        </div>
    );
};

export default ApplicationStatusView;
